import Autocomplete from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { Controller, Control, useFormContext } from 'react-hook-form';
import { HNISO3166Alpha3CountryNames, ListType } from 'app/pages/cards/spend-rules/utils';
import { HNISO3166Alpha3Country, MerchantSpendRule } from 'API';
import { Box, Typography, FormControl, Select, MenuItem, TextField } from '@mui/material';

interface SpendingByCountryProps {
  control: Control<any>;
  selectedSpendRule?: MerchantSpendRule;
  isDisabled?: boolean;
}

const SpendingByCountry = ({
  control,
  selectedSpendRule,
  isDisabled,
}: SpendingByCountryProps): JSX.Element => {
  const { t } = useTranslation();

  const { setValue, watch } = useFormContext();
  const typeValue = watch('spendingByCountryType');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: '16px',
        backgroundColor: (theme) => theme.palette.primary.lighter,
        borderRadius: '16px',
      }}
    >
      <Typography variant="h4" color="primary.main" mb={3.5}>
        {t('spendRules.spendingByCountry')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: '24px',
          backgroundColor: '#fff',
          borderRadius: '16px',
        }}
      >
        <Controller
          name="spendingByCountryType"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <Select
                {...field}
                onChange={(e) => {
                  const val = e.target.value as ListType;
                  field.onChange(val);
                  if (val === ListType.NO_RESTRICTIONS) {
                    setValue('spendingByCountry', []);
                  }
                }}
                variant="standard"
                disableUnderline
                disabled={isDisabled}
              >
                <MenuItem value={ListType.NO_RESTRICTIONS}>{t('spendRules.noRestrictions')}</MenuItem>
                <MenuItem value={ListType.BLOCK_LIST}>{t('spendRules.allCountriesExcept')}</MenuItem>
                <MenuItem value={ListType.ALLOW_LIST}>{t('spendRules.noCountriesExcept')}</MenuItem>
              </Select>
            </FormControl>
          )}
        />

        {typeValue !== ListType.NO_RESTRICTIONS && (
          <Box mt={1}>
            <Controller
              name="spendingByCountry"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={Object.values(HNISO3166Alpha3Country)}
                  getOptionLabel={(code) => HNISO3166Alpha3CountryNames[code] || code}
                  filterSelectedOptions
                  disabled={isDisabled}
                  value={field.value ?? []}
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                    />
                  )}
                />
              )}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SpendingByCountry;
